<template>
  <div :class="operateIconClass">
    <!-- 显示的icon -->
    <ll-icon-base
      v-for="(item, index) in showIconList"
      v-bind="item"
      :key="item.iconName"
      :data="data"
      :is-show="
        Object.keys(item).includes('isShow') ? item.isShow! : true
      "
      :tooltip-class="bem.e(marginButtonClass(index, showIconList.length - 1))"
      :tooltip-props="tooltipProps"
    />
    <template v-if="modalIconList.length === 1">
      <ll-icon-base
        v-for="(item, index) in modalIconList"
        :key="index"
        v-bind="item"
        :data="data"
        :tooltip-class="`${bem.e('last-icon')}`"
        :tooltip-props="tooltipProps"
      />
    </template>
    <!-- 下拉提示的icon -->
    <a-dropdown
      v-if="showDropdown && modalIconList.length > 1"
      :overlay-class-name="`${bem.e('dropdown')} ${props.overlayClassName}`"
      :open="dropdownOpen"
      :trigger="trigger"
      :get-popup-container="props.getPopupContainer"
      @open-change="handleDropdownOpenChange"
    >
      <div :class="bem.e('dropdown-container')">
        <ll-tooltip title="更多" v-bind="tooltipProps">
          <ll-icon-base
            :is-show-tooltip="false"
            :is-show-container="
              showIconList[0] ? showIconList[0]?.isShowContainer : true
            "
            :icon-type="showIconList[0] ? showIconList[0]?.iconType : 'primary'"
            :data="data"
            icon-name="icon-more"
            :class="bem.e('icon-more')"
          />
        </ll-tooltip>
      </div>
      <template #overlay>
        <a-menu @click="handleDropdownParentClick">
          <slot>
            <template v-for="item in modalIconList">
              <a-menu-item
                v-if="Object.keys(item).includes('isShow') ? item.isShow! : true"
                :key="item.iconName"
              >
                <ll-icon-base
                  v-bind="item"
                  :data="data"
                  :icon-name="showDropdownItemIcon ? item.iconName : ''"
                />
              </a-menu-item>
            </template>
          </slot>
        </a-menu>
      </template>
    </a-dropdown>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { createNamespace } from '@ll-plus/utils'

import { IconBaseProps, LlIconBase } from '@ll-plus/components'
import { operateIconEmits, operateIconProps } from './operate-icon'

// 1.defineOptions
defineOptions({
  name: 'LlOperateIcon',
})

// 2.props/emits
const props = defineProps(operateIconProps)
const emits = defineEmits(operateIconEmits)

// 3.ref/const
const showIconList = ref<IconBaseProps[]>([]) // 显示的icon列表
const modalIconList = ref<IconBaseProps[]>([]) // 弹窗的icon列表
const dropdownOpen = ref(false) // 下拉菜单的open
const defaultList = ref([
  // 默认的icon列表
  {
    iconType: computed(() => props.iconType || 'primary'), // default默认为灰色
    isShowContainer: false, // 是否显示icon容器
    iconName: 'icon-edit',
    tooltipTitle: '编辑',
    disabled: props.disabled,
    onClick: (e: Event, data: any) => {
      emits('edit', e, data)
    },
  },
  {
    iconType: computed(() => props.iconType || 'primary'), // default默认为灰色
    isShowContainer: false, // 是否显示icon容器
    iconName: 'icon-delete',
    tooltipTitle: '删除',
    disabled: props.disabled,
    onClick: (e: Event, data: any) => {
      emits('delete', e, data)
    },
  },
])
const bem = createNamespace('operate-icon')
const operateIconClass = computed(() => {
  const isHasDropdown = props.showDropdown && modalIconList.value.length
  return `${bem.b()} ${
    isHasDropdown ? bem.m('has-dropdown') : bem.m('no-dropdown')
  }`
})
// 4.methods
// 点击更多按钮
const marginButtonClass = (index, length) => {
  return index < length ? 'margin-tooltip' : ''
}
const handleOpenChange = (open: boolean) => emits('onDropdownOpenChange', open)
// 下拉菜单open
const handleDropdownOpenChange = (open: boolean) => {
  dropdownOpen.value = open
  handleOpenChange(open)
}
const handleDropdownParentClick = () => {
  dropdownOpen.value = false
  handleOpenChange(false)
}

const closeDropdown = () => {
  dropdownOpen.value = false
}

// 5.watch
watch(
  () => [props.list, props.showDropdown],
  (data: any) => {
    const [newList, showDropdown] = data
    const list =
      newList && newList.length
        ? newList.filter(Boolean).filter((item) => {
            const { isShow, disabled } = item
            const d =
              typeof disabled === 'function' ? disabled(props.data) : disabled
            const show =
              typeof isShow === 'function' ? isShow(props.data) : isShow
            item.disabled = d
            item.isShow = show
            return show
          })
        : defaultList.value
    const length = list.length
    if (length >= props.showIconLimit && showDropdown) {
      showIconList.value = list
        .slice(0, props.showIconLimit - 1)
        .map((item) => ({ ...item, isShowTooltip: true })) as IconBaseProps[]
      // 默认modal里面要icon后面跟文字
      const sliceList = list.slice(props.showIconLimit - 1)
      if (sliceList.length === 1) {
        modalIconList.value = sliceList.map((item: any) => ({
          ...item,
          isShowTitle: false,
          isShowTooltip: true,
          isShowContainer: false,
        }))
      } else {
        modalIconList.value = sliceList.map((item: any) => ({
          ...item,
          isShowTitle: true,
        }))
      }
    } else {
      showIconList.value = list.map((item: any) => ({
        ...item,
        isShowTooltip: true,
      })) as IconBaseProps[]
    }
  },
  { immediate: true, deep: true }
)
defineExpose({
  closeDropdown,
})
</script>
